/**
 * The To Do list, read once for the whole frame (2026-09-24).
 *
 * TWO THINGS SHOW IT: the count on the Home tab, on every screen, and the list
 * itself on Home. They read the same fact, so they read it from here. A screen
 * that reads one fact twice has to remember to refresh both, and the way never
 * to forget is to read it once (CLAUDE.md, "A screen that reads the same fact
 * twice must refresh both").
 *
 * READ ON EVERY NAVIGATION. The count is about work done elsewhere -- a
 * transaction categorized on Entries, a deposit coded on the tracker -- and the
 * moment it has to be right is the moment the operator looks up from that work.
 * Keyed on the path, as NavMenu's close is, so a filter changed inside a screen
 * reads nothing. `reload` is for a change made without navigating: Mark Filed.
 *
 * ONLY THE LATEST READ LANDS. Two quick navigations start two reads, and the
 * slower one must not overwrite the newer answer with an older one.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ApiError, api, type TodoList } from '../api.js';

export interface TodoState {
  /** Null until the first read lands. */
  todo: TodoList | null;
  /** The server's sentence when the latest read failed. The badge says nothing; Home says this. */
  error: string | null;
  /** Read the list again. Resolves once it has landed. */
  reload: () => Promise<void>;
}

const TodoContext = createContext<TodoState | null>(null);

export function TodoProvider({ children }: { children: ReactNode }) {
  const [todo, setTodo] = useState<TodoList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { pathname } = useLocation();
  const latest = useRef(0);

  const reload = useCallback(async () => {
    latest.current += 1;
    const read = latest.current;
    try {
      const { todo: next } = await api.getTodo();
      if (read !== latest.current) return;
      setTodo(next);
      setError(null);
    } catch (caught) {
      if (read !== latest.current) return;
      setError(caught instanceof ApiError ? caught.message : 'Could not read the To Do list.');
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [pathname, reload]);

  // A read still in flight when the frame goes away lands nowhere.
  useEffect(
    () => () => {
      latest.current += 1;
    },
    [],
  );

  const value = useMemo(() => ({ todo, error, reload }), [todo, error, reload]);
  return <TodoContext.Provider value={value}>{children}</TodoContext.Provider>;
}

/** The frame's To Do list. Only inside `TodoProvider`, which the shell gives every screen. */
export function useTodo(): TodoState {
  const state = useContext(TodoContext);
  if (state === null) {
    throw new Error('useTodo is used outside a TodoProvider; AppShell provides one to every screen.');
  }
  return state;
}
